/**
 * Grown-up playback controls — the app's watch dock (frontend-app
 * `WatchControls`, drawn the same way by FabroLabs/story-player): play and the
 * two 10-second skips, the seek line with "0:27 / 8:32", then speed, subtitles
 * and full screen; one row from `DOCK_ONE_ROW_PX` up, two on a phone. Keyboard
 * shortcuts sit behind it (the "video player" layer on top of the §8.6 kid HUD).
 *
 * The kid HUD stays what §8.6 says it is — pause while a line plays, ▶ / ↺ at
 * holds, the pictures-only overlay, the 2 s long-press panel. This layer is for
 * the grown-up sitting next to the child, and it is built so the child's side
 * cannot tell it is there:
 *
 * - **It is never a stage tap.** Every pointer event on the bar stops at the
 *   bar, and a seek travels through `HudPort.onSeek`, not `onTap`, so the
 *   runner's tap policy (swallow during a line, continue at a hold, the 300 ms
 *   debounce) is untouched.
 * - **It gets out of the way.** Like a video player it appears on pointer
 *   movement, a tap or a key, and hides after `CONTROLS_HIDE_MS` of nothing
 *   while the story plays. It stays up while paused, at a hold and at the end.
 *   `prefers-reduced-motion` (and the panel's switch) turns the slide off.
 * - **CSS pixels, not stage pixels.** Every button is a 44 px square and the
 *   line is 32 px tall, whatever `--stage-scale` says.
 *
 * - **Speed.** A "1×" button opens a menu of 0.5×-2× (`PLAYBACK_RATES`), and
 *   `<` / `>` step through it; the choice flashes on screen, and any speed but
 *   1× stays mint on the button. The HUD turns it
 *   into `changePrefs({ rate })`, the player hands it to the clock and the
 *   voice, and the mount remembers it.
 *
 * `hud.ts` owns the instance: it feeds `setState` from `PlayerUiState`, calls
 * `place()` from its own layout pass (so ▶ / ↺, the gear and the subtitle band
 * move out of the bar's way) and implements `ControlBarActions` with the
 * emitters it already has. The pure helpers at the top are what the tests pin.
 *
 * `setState` runs once per frame while a story plays, so it only ever writes
 * what changed, and the seek bar moves by `transform` (a compositor-only
 * change) — a `width` / `left` per frame re-laid the whole page out.
 */

import { PLAYBACK_RATES, clampRate, formatRate, stepRate } from './clock'
import type { SeekRequest } from './index'
import type { Phase } from './runner'
import { THEME } from './theme'

export { PLAYBACK_RATES, formatRate, stepRate }

/* ================================================================== pure */

/** Idle time before the bar hides while the story plays. */
export const CONTROLS_HIDE_MS = 3000
/** ← / → */
export const SEEK_STEP_S = 5
/** J / L */
export const SEEK_JUMP_S = 10
/** From this picture width up the dock is one row; below it, the line and then the buttons. */
export const DOCK_ONE_ROW_PX = 600
/** The one-row dock: 44 px buttons and 8 px above and below. */
export const DOCK_ROW_H = 60
/** The phone dock: the line's row and the buttons' row. */
export const DOCK_TWO_ROWS_H = 88
/** The fade over the picture above the dock, so the line never sits on a hard edge. */
export const DOCK_FADE_PX = 24
/** The one-row bar over the picture, fade included (`controlBarHeight` has every case). */
export const CONTROL_BAR_H = DOCK_ROW_H + DOCK_FADE_PX
/** Minimum hit target for anything in the bar (WCAG 2.5.5 / Apple HIG). */
export const CONTROL_HIT_PX = 44
/** Below this bar width the phone dock's line keeps its thumb clear of the edges. */
export const NARROW_BAR_PX = 480
/** Space either side of a phone dock's line, so its thumb at 0:00 never touches the bar's edge. */
export const NARROW_TRACK_MARGIN_PX = 6
/** Below this bar width (a 320 px phone) the dock's sides give up half their padding. */
export const TINY_BAR_PX = 360

/**
 * The time text's width in em — tabular digits, rounded up — so the time can
 * hold the width of the story's whole length and the line beside it never
 * twitches when 9:59 becomes 10:00. An estimate on purpose: measuring would
 * force a layout.
 */
export function timeTextEm(text: string): number {
  let em = 0
  for (const ch of text) em += ch >= '0' && ch <= '9' ? 0.58 : ch === ':' ? 0.3 : ch === '/' ? 0.38 : ch === ' ' ? 0.26 : 0.6
  return em
}

/** The widest speed label ("0.75×", "1.25×") in em at the button's weight. */
const RATE_LABEL_EM = 2.75

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)

/** Whole seconds, never negative, tolerant of float dust (11.9999999 is 12). */
function wholeSeconds(seconds: number): number {
  return Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds + 1e-6) : 0
}

/**
 * `m:ss`, or `h:mm:ss` from an hour up. Floors, so elapsed and total agree at
 * the end ("0:28 / 0:28", never "0:29 / 0:28").
 */
export function formatTime(seconds: number): string {
  const s = wholeSeconds(seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`

/** "1 minute 5 seconds", "12 seconds", "2 minutes". */
export function spokenDuration(seconds: number): string {
  const s = wholeSeconds(seconds)
  const m = Math.floor(s / 60)
  const sec = s % 60
  if (m === 0) return plural(sec, 'second')
  if (sec === 0) return plural(m, 'minute')
  return `${plural(m, 'minute')} ${plural(sec, 'second')}`
}

/**
 * The seek slider's `aria-valuetext`: "12 seconds of 34". A story under a
 * minute says the unit once; a longer one says it in full both times
 * ("1 minute 5 seconds of 2 minutes 10 seconds").
 */
export function seekValueText(elapsed: number, total: number): string {
  const e = wholeSeconds(elapsed)
  const T = wholeSeconds(total)
  if (T < 60) return `${plural(e, 'second')} of ${T}`
  return `${spokenDuration(e)} of ${spokenDuration(T)}`
}

export interface TrackRect {
  left: number
  width: number
}

/** Pointer x → fraction of the track, clamped to [0, 1]. A zero-width track reads 0. */
export function fractionAt(clientX: number, rect: TrackRect): number {
  if (!(rect.width > 0) || !Number.isFinite(clientX) || !Number.isFinite(rect.left)) return 0
  return clamp((clientX - rect.left) / rect.width, 0, 1)
}

/** Pointer x → story seconds, clamped to the story. */
export function timeAtPointer(clientX: number, rect: TrackRect, total: number): number {
  const T = Number.isFinite(total) && total > 0 ? total : 0
  return fractionAt(clientX, rect) * T
}

/* ------------------------------------------------------------- hotkeys */

export type HotkeyAction =
  | { kind: 'toggle' }
  | { kind: 'seekBy'; seconds: number }
  | { kind: 'seekTo'; where: 'start' | 'end' }
  | { kind: 'scene'; delta: -1 | 1 }
  | { kind: 'mute' }
  | { kind: 'subtitles' }
  | { kind: 'speed'; delta: -1 | 1 }
  | { kind: 'fullscreen' }
  | { kind: 'help' }
  | { kind: 'escape' }

/** The slice of a `KeyboardEvent` the mapping reads — structural, so tests pass plain objects. */
export interface KeyLike {
  key: string
  code?: string
  ctrlKey?: boolean
  metaKey?: boolean
  altKey?: boolean
  isComposing?: boolean
  repeat?: boolean
  defaultPrevented?: boolean
  target?: EventTarget | null
}

interface ElementLike {
  tagName?: string
  isContentEditable?: boolean
  getAttribute?(name: string): string | null
}

/** True when a key press belongs to whatever has focus: a text field, a select, an editable region. */
export function isTypingTarget(target: EventTarget | null | undefined): boolean {
  const el = target as ElementLike | null | undefined
  if (!el || typeof el.tagName !== 'string') return false
  const tag = el.tagName.toUpperCase()
  // Every <input>, not only text: arrow keys on the panel's volume slider must
  // move the slider, not the story.
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (el.isContentEditable) return true
  const editable = el.getAttribute?.('contenteditable')
  if (editable === '' || editable === 'true' || editable === 'plaintext-only') return true
  const role = el.getAttribute?.('role')
  return role === 'textbox' || role === 'searchbox' || role === 'combobox'
}

/**
 * True when the browser will turn this key into a click on the focused
 * element — Space / Enter on a button or a link. The HUD's own buttons answer
 * that click, so a hotkey here would fire the same intention twice.
 */
function activatesNatively(e: KeyLike): boolean {
  if (e.key !== ' ' && e.key !== 'Enter' && e.key !== 'Spacebar') return false
  const el = e.target as ElementLike | null | undefined
  if (!el || typeof el.tagName !== 'string') return false
  const tag = el.tagName.toUpperCase()
  if (tag === 'BUTTON' || tag === 'SUMMARY') return true
  if (tag === 'A' && el.getAttribute?.('href') != null) return true
  return el.getAttribute?.('role') === 'button'
}

/** `e.key`, lower-cased for letters, with the physical key as a fallback on non-Latin layouts. */
function keyOf(e: KeyLike): string {
  const key = e.key === 'Spacebar' ? ' ' : e.key
  if (key.length === 1) {
    const lower = key.toLowerCase()
    if (/^[a-z]$/.test(lower)) return lower
    // A Cyrillic or Greek layout sends "л" for the K key; the grown-up still
    // expects K to pause. Only letters fall back — punctuation is layout-true.
    if (/\p{L}/u.test(key) && e.code && /^Key[A-Z]$/.test(e.code)) return e.code.slice(3).toLowerCase()
    return key
  }
  return key
}

const TOGGLE: HotkeyAction = { kind: 'toggle' }

/**
 * A key press → what it means, or `null` when it is not ours to handle.
 *
 * Never ours: a key someone else already handled (`defaultPrevented` — the
 * seek slider's own arrows), an IME composition, anything with Ctrl / ⌘ / Alt
 * (browser and OS shortcuts), anything typed into a field, and Space / Enter
 * on a focused button. Held keys repeat only for the seeks.
 */
export function hotkeyFor(e: KeyLike): HotkeyAction | null {
  if (e.defaultPrevented || e.isComposing) return null
  if (e.ctrlKey || e.metaKey || e.altKey) return null
  if (isTypingTarget(e.target)) return null
  if (activatesNatively(e)) return null

  let action: HotkeyAction | null = null
  switch (keyOf(e)) {
    case ' ':
    case 'k':
      action = TOGGLE
      break
    case 'ArrowLeft':
      action = { kind: 'seekBy', seconds: -SEEK_STEP_S }
      break
    case 'ArrowRight':
      action = { kind: 'seekBy', seconds: SEEK_STEP_S }
      break
    case 'j':
      action = { kind: 'seekBy', seconds: -SEEK_JUMP_S }
      break
    case 'l':
      action = { kind: 'seekBy', seconds: SEEK_JUMP_S }
      break
    case 'Home':
      action = { kind: 'seekTo', where: 'start' }
      break
    case 'End':
      action = { kind: 'seekTo', where: 'end' }
      break
    case '[':
      action = { kind: 'scene', delta: -1 }
      break
    case ']':
      action = { kind: 'scene', delta: 1 }
      break
    case 'm':
      action = { kind: 'mute' }
      break
    case 'c':
      action = { kind: 'subtitles' }
      break
    case '<':
      action = { kind: 'speed', delta: -1 }
      break
    case '>':
      action = { kind: 'speed', delta: 1 }
      break
    case 'f':
      action = { kind: 'fullscreen' }
      break
    case '?':
      action = { kind: 'help' }
      break
    case 'Escape':
      action = { kind: 'escape' }
      break
    default:
      return null
  }
  if (e.repeat && action.kind !== 'seekBy') return null
  return action
}

/** The rows of the `?` sheet — also the documentation of the mapping above. */
export const SHORTCUTS: readonly (readonly [keys: string, what: string])[] = [
  ['Space  K', 'Play or pause'],
  ['←  →', `Back or forward ${SEEK_STEP_S} seconds`],
  ['J  L', `Back or forward ${SEEK_JUMP_S} seconds`],
  ['Home  End', 'Start or end of the story'],
  ['[  ]', 'Part before or next part'],
  ['<  >', 'Slower or faster'],
  ['M', 'Sound off or on'],
  ['C', 'Subtitles on or off'],
  ['F', 'Full screen'],
  ['?', 'Show or hide this list'],
]

/* =================================================================== DOM */

/** What the bar renders from — a slice of `PlayerUiState` plus what only the HUD knows. */
export interface ControlBarView {
  phase: Phase
  storyT: number
  storyDuration: number
  sceneStarts: readonly number[]
  scrubbing: boolean
  needsGesture: boolean
  /** False under the start screen, the panel, the dim sheet and the error page. */
  available: boolean
  /** False while the grown-up panel or the error page is up. */
  keysEnabled: boolean
  reducedMotion: boolean
  lowDistraction?: boolean
  /** Subtitles on: drives the CC button's pressed state. */
  subtitles: boolean
  /** Playback speed, 0.5-2 — the speed button's label. Absent reads 1. */
  rate?: number
}

/** What the bar asks of the HUD. Every one maps onto an emitter the HUD already has. */
export interface ControlBarActions {
  seek(req: SeekRequest): void
  /** Space / K. The HUD decides what "play/pause" means in its phase. */
  togglePlay(): void
  scene(delta: -1 | 1): void
  /** Returns the new state, for the on-screen confirmation. */
  toggleMute(): boolean
  toggleSubtitles(): boolean
  /** The speed menu or `<` / `>`. Returns the speed now in effect. Optional: a HUD without it has no speed control. */
  setRate?(rate: number): number
  /** The bar appeared, hid or moved on its own (a timer, a pointer): lay the HUD out again. */
  changed(): void
}

export interface ControlBarOptions {
  /** The player host — the fullscreen target and the hotkeys' scope. */
  host: HTMLElement
  actions: ControlBarActions
  /**
   * Whether a key pressed with nothing focused belongs to the player. True for
   * the kid mode (the player covers the whole viewport); false for an inline
   * preview until the grown-up touches it.
   */
  engaged?: boolean
  hideAfterMs?: number
}

/** Where `hud.ts` puts the bar, in host CSS px. */
export interface ControlBarPlacement {
  /** `over`: across the bottom of the picture. `below`: in the letterbox directly under it. */
  mode: 'over' | 'below'
  left: number
  width: number
  stageTop: number
  stageBottom: number
  hostHeight: number
  /** On-screen confirmation and shortcut-sheet text size — `HUD_FLOOR.text` or more. */
  textPx: number
}

/**
 * The bar's height for a picture `width` px wide: the dock (one row from
 * `DOCK_ONE_ROW_PX` up, two on a phone) plus, over the picture, the fade above
 * it. Pure, so the HUD can choose `over` / `below` before the bar is placed.
 */
export function controlBarHeight(width: number, mode: 'over' | 'below' = 'over'): number {
  const dock = width >= DOCK_ONE_ROW_PX ? DOCK_ROW_H : DOCK_TWO_ROWS_H
  return dock + (mode === 'over' ? DOCK_FADE_PX : 0)
}

/**
 * The app's glyphs (frontend-app `Icon` / story-player `--icon-*`), inline and
 * in `currentColor`: no icon font, no network.
 */
const skipGlyph = (forward: boolean): string =>
  '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">' +
  `<g${forward ? ' transform="translate(32 0) scale(-1 1)"' : ''}>` +
  '<path d="M16 5a12 12 0 1 1-11.5 8.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
  '<path d="m10 5 7-4v8z" fill="currentColor"/></g>' +
  '<text x="16" y="22" text-anchor="middle" font-family="sans-serif" font-size="12" font-weight="500" fill="currentColor">10</text></svg>'
const ICON = {
  play: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5.8 19.2 12 8 18.2Z" fill="currentColor" stroke="currentColor" stroke-width="3.2" stroke-linejoin="round"/></svg>',
  pause: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>',
  replay: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 2.64-6.36L3 8.3"/><path d="M3 3.5v4.8h4.8"/></svg>',
  back: skipGlyph(false),
  forward: skipGlyph(true),
  expand: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
  collapse: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>',
} as const

const STYLE_ID = 'sp-controls-style'
/** On the host while it fills the window in place of real full screen; `-lock` on <html> meanwhile. */
const PSEUDO_FULLSCREEN_ATTR = 'data-sp-fullscreen'
/**
 * On the host while that window-filling full screen is drawn sideways (an upright phone): the
 * HUD reads it to measure the picture in the host's own axes (`hud.ts#measure`).
 */
export const ROTATED_ATTR = 'data-sp-rotated'
/**
 * The message a player in a frame (the app's dynamic story page) posts to the page around it
 * when its window-filling full screen goes on or off — `{ type, on }`, nothing else. Filling
 * its own window fills only the frame's box on an iPhone, so the page pins the frame over the
 * whole screen meanwhile; the page posts the same message back to end it from outside.
 */
export const FRAME_FULLSCREEN_MESSAGE = 'studio-player:fullscreen'

/*
 * The app's watch dock. Two arrangements, chosen by the width of the PICTURE
 * as the app chooses them: a phone's two rows — the line, then the buttons
 * spread across it in six columns — and one row from `DOCK_ONE_ROW_PX` up:
 * play and the two skips, the line with its times, then speed, subtitles and
 * full screen.
 */
const CSS = `
.sp-ctl { position: absolute; box-sizing: border-box; pointer-events: auto; color: ${THEME.dockText};
  font-family: ${THEME.font}; touch-action: manipulation; -webkit-tap-highlight-color: transparent;
  display: flex; flex-direction: column; justify-content: flex-end;
  transition: opacity 160ms ease, visibility 0s linear 0s; }
.sp-ctl *, .sp-ctl *::before, .sp-ctl *::after { box-sizing: border-box; }
.sp-ctl [hidden] { display: none !important; }
.sp-ctl[data-mode="over"] { padding-top: ${DOCK_FADE_PX}px;
  background: linear-gradient(0deg, rgb(3 22 18 / 0.82), rgb(3 22 18 / 0.44) 65%, transparent); }
.sp-ctl[data-mode="below"] { background: transparent; }
.sp-ctl[data-shown="0"] { opacity: 0; visibility: hidden; pointer-events: none;
  transition: opacity 160ms ease, visibility 0s linear 160ms; }

.sp-ctl-dock { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); align-items: center;
  min-height: ${DOCK_TWO_ROWS_H}px; padding: 6px 16px; }
.sp-ctl-timeline { grid-column: 1 / -1; grid-row: 1; display: flex; align-items: center; gap: 16px; min-width: 0; }
.sp-ctl-transport, .sp-ctl-side { display: contents; }

.sp-ctl-btn { position: relative; display: grid; place-items: center; width: ${CONTROL_HIT_PX}px; height: ${CONTROL_HIT_PX}px;
  margin: 0; padding: 6px; border: 0; border-radius: 10px; background: transparent; color: inherit;
  font: inherit; line-height: 1; cursor: pointer; -webkit-tap-highlight-color: transparent;
  grid-row: 2; justify-self: center; transition: background-color 120ms ease, color 120ms ease, opacity 120ms ease; }
.sp-ctl-btn:hover:not(:disabled), .sp-ctl-btn[aria-expanded="true"] { background: ${THEME.hover}; }
.sp-ctl-btn:disabled { opacity: 0.5; cursor: default; }
.sp-ctl-btn:focus-visible, .sp-ctl-seek:focus-visible { outline: 2px solid ${THEME.accent}; outline-offset: 3px; }
.sp-ctl-btn svg { display: block; width: 32px; height: 32px; pointer-events: none; }
.sp-ctl-btn[data-hud="play-pause"] { grid-column: 1; justify-self: start; }
.sp-ctl-btn[data-hud="back"] { grid-column: 2; }
.sp-ctl-btn[data-hud="forward"] { grid-column: 3; }
.sp-ctl-speed { grid-row: 2; grid-column: 4; justify-self: center; position: relative; display: flex; }
.sp-ctl-btn[data-hud="subtitles"] { grid-column: 5; }
.sp-ctl-btn[data-hud="fullscreen"] { grid-column: 6; justify-self: end; }
/* Without the full-screen button the last column is subtitles'. */
.sp-ctl[data-fullscreen="0"] .sp-ctl-dock { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.sp-ctl[data-fullscreen="0"] .sp-ctl-btn[data-hud="subtitles"] { justify-self: end; }

/* The line: a 7 px rail a finger can find, 32 px tall. */
.sp-ctl-seek { position: relative; flex: 1; min-width: 0; height: 32px; cursor: pointer; touch-action: none;
  outline: none; border-radius: 999px; }
.sp-ctl-seek[aria-disabled="true"] { cursor: default; }
.sp-ctl-rail { position: absolute; left: 0; right: 0; top: 50%; height: 7px; margin-top: -3.5px;
  border-radius: 999px; background: ${THEME.rail}; overflow: hidden; }
.sp-ctl-fill { position: absolute; left: 0; top: 0; bottom: 0; width: 100%; background: ${THEME.progress};
  transform-origin: 0 50%; transform: scaleX(0); will-change: transform; }
.sp-ctl-mark { position: absolute; top: 0; bottom: 0; width: 2px; margin-left: -1px; background: rgb(3 22 18 / 0.8); }
/* A track-wide layer, so translateX(n%) of it is n % of the track: the thumb moves without layout. */
.sp-ctl-thumbs { position: absolute; left: 0; right: 0; top: 0; bottom: 0; pointer-events: none; will-change: transform; }
.sp-ctl-thumb { position: absolute; top: 50%; left: 0; width: 18px; height: 18px; margin: -9px 0 0 -9px;
  border-radius: 50%; background: ${THEME.thumb}; pointer-events: none; transition: transform 120ms ease; }
.sp-ctl[data-dragging="1"] .sp-ctl-thumb, .sp-ctl-seek:focus-visible .sp-ctl-thumb { transform: scale(1.2); }

/* "0:27 / 8:32", after the line. */
.sp-ctl-time { flex-shrink: 0; font-size: 15px; line-height: 22px; text-align: right; white-space: nowrap;
  font-variant-numeric: tabular-nums; font-feature-settings: "tnum" 1; }

/* Subtitles: the letters in a box, filled while they are on. */
.sp-ctl-cc { display: grid; place-items: center; width: 32px; height: 28px; border: 2px solid currentColor;
  border-radius: 4px; font-size: 17px; font-weight: 700; line-height: 22px; text-transform: uppercase; }
.sp-ctl-btn[aria-pressed="true"] .sp-ctl-cc { color: ${THEME.ccOnText}; background: ${THEME.ccOn}; border-color: ${THEME.ccOn}; }

/* Speed: the rate itself is the label; anything but 1x is mint, so a changed speed is seen. */
.sp-ctl-btn.sp-ctl-rate { width: auto; min-width: ${CONTROL_HIT_PX}px; padding: 6px 4px; font-size: 16px; font-weight: 700;
  white-space: nowrap; font-variant-numeric: tabular-nums; font-feature-settings: "tnum" 1; }
.sp-ctl-rate[data-changed="1"] { color: ${THEME.accent}; }
.sp-ctl-menu { position: absolute; left: 50%; bottom: calc(100% + 10px); transform: translateX(-50%);
  display: flex; flex-direction: column; gap: 2px; min-width: 132px; padding: 6px; border-radius: 12px;
  border: 1px solid ${THEME.line}; background: ${THEME.sheet}; color: ${THEME.text};
  box-shadow: 0 16px 40px -12px rgba(0,0,0,0.85); pointer-events: auto; }
.sp-ctl-menu[data-dir="down"] { bottom: auto; top: calc(100% + 10px); }
.sp-ctl-menu-head { padding: 6px 10px 4px; color: ${THEME.textDim}; font-size: 11px; font-weight: 700;
  letter-spacing: 0.16em; text-transform: uppercase; white-space: nowrap; }
.sp-ctl-menu button { display: flex; align-items: center; gap: 10px; min-height: 40px; margin: 0; padding: 0 12px;
  border: 0; border-radius: 8px; background: transparent; color: inherit; font: inherit; font-size: 15px; font-weight: 600;
  text-align: left; font-variant-numeric: tabular-nums; cursor: pointer; white-space: nowrap; }
.sp-ctl-check { width: 1em; flex: none; text-align: center; }
.sp-ctl-menu button[aria-checked="true"] { color: ${THEME.accent}; }
.sp-ctl-menu button[aria-checked="true"] .sp-ctl-check::before { content: "✓"; }
.sp-ctl-menu button:hover { background: ${THEME.hover}; }
.sp-ctl-menu button:focus-visible { outline: 2px solid ${THEME.accent}; outline-offset: -2px; }

/* From DOCK_ONE_ROW_PX up, one row: play and the skips, the line with its times, then the toggles. */
.sp-ctl[data-size="wide"] .sp-ctl-dock { grid-template-columns: auto minmax(0, 1fr) auto; gap: 12px;
  min-height: ${DOCK_ROW_H}px; padding: 8px 16px; }
.sp-ctl[data-size="wide"] .sp-ctl-transport, .sp-ctl[data-size="wide"] .sp-ctl-side { display: flex; align-items: center; gap: 8px; }
.sp-ctl[data-size="wide"] .sp-ctl-transport { grid-column: 1; grid-row: 1; }
.sp-ctl[data-size="wide"] .sp-ctl-timeline { grid-column: 2; gap: 12px; }
.sp-ctl[data-size="wide"] .sp-ctl-side { grid-column: 3; grid-row: 1; justify-content: flex-end; }
.sp-ctl[data-size="wide"] .sp-ctl-btn { padding: 4px; }
.sp-ctl[data-size="wide"] .sp-ctl-btn svg { width: 30px; height: 30px; }
.sp-ctl[data-size="wide"] .sp-ctl-cc { width: 28px; height: 24px; font-size: 16px; line-height: 20px; }
.sp-ctl[data-size="wide"] .sp-ctl-btn.sp-ctl-rate { padding: 4px; }
/* A 320 px phone: six columns of 44 px still fit once the sides give up half their room. */
.sp-ctl[data-size="tiny"] .sp-ctl-dock { padding-left: 8px; padding-right: 8px; }

.sp-ctl-osd { position: absolute; left: 50%; transform: translateX(-50%); pointer-events: none;
  padding: 6px 14px; border-radius: 8px; background: ${THEME.caption}; color: #fff; font-family: ${THEME.font};
  font-weight: 500; white-space: nowrap; transition: opacity 160ms ease; }
.sp-ctl-osd[data-shown="0"] { opacity: 0; }
.sp-ctl-help { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
  width: min(560px, calc(100% - 32px)); max-height: calc(100% - 32px); overflow-y: auto; box-sizing: border-box;
  padding: 16px 20px; border-radius: 16px; border: 1px solid ${THEME.line}; background: ${THEME.shell}; color: ${THEME.text};
  font-family: ${THEME.font}; box-shadow: 0 16px 48px -12px rgba(0,0,0,0.8); pointer-events: auto; }
.sp-ctl-help-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 8px; }
.sp-ctl-help h2 { margin: 0; font-size: 1em; font-weight: 700; }
.sp-ctl-help table { width: 100%; border-collapse: collapse; }
.sp-ctl-help th { text-align: left; white-space: nowrap; padding: 6px 16px 6px 0; font-weight: 700; color: ${THEME.accent}; }
.sp-ctl-help td { padding: 6px 0; color: ${THEME.textDim}; font-size: 0.8em; }
.sp-ctl-help button { min-width: ${CONTROL_HIT_PX}px; min-height: ${CONTROL_HIT_PX}px; border-radius: 999px;
  background: ${THEME.hover}; color: inherit; font: inherit; font-size: 0.8em; border: 1px solid ${THEME.line}; padding: 0 16px; cursor: pointer; }
.sp-ctl-help button:focus-visible { outline: 2px solid ${THEME.accent}; outline-offset: 3px; }
/* Full screen where the browser has none for an element (an iPhone's Safari): the player
   fills the window instead. Important throughout, because the stage writes an inline
   position on the host and a page may size it inline too. */
[${PSEUDO_FULLSCREEN_ATTR}="1"] { position: fixed !important; inset: 0 !important; width: auto !important; height: auto !important;
  max-width: none !important; max-height: none !important; margin: 0 !important; border-radius: 0 !important;
  transform: none !important; z-index: 2147483000 !important; background: ${THEME.shell} !important; }
html[${PSEUDO_FULLSCREEN_ATTR}-lock], html[${PSEUDO_FULLSCREEN_ATTR}-lock] body { overflow: hidden !important; overscroll-behavior: none; }
/* An upright phone: the story is laid out landscape (the window's height wide, its width tall)
   and turned a quarter clockwise about its top-left corner, parked at the window's right edge —
   so it fills the screen sideways, the way a phone's own video player does, rotation lock or
   not. dvh, where there is one, is the height left beside Safari's bars. */
[${PSEUDO_FULLSCREEN_ATTR}="1"][${ROTATED_ATTR}="1"] { inset: auto !important; top: 0 !important; left: 100vw !important;
  width: 100vh !important; width: 100dvh !important; height: 100vw !important; height: 100dvw !important;
  transform: rotate(90deg) !important; transform-origin: 0 0 !important; }
@media (prefers-reduced-motion: reduce) {
  .sp-ctl, .sp-ctl[data-shown="0"], .sp-ctl-thumb, .sp-ctl-osd, .sp-ctl-btn { transition: none; }
}
.sp-ctl[data-reduced-motion="1"], .sp-ctl[data-reduced-motion="1"][data-shown="0"],
.sp-ctl[data-reduced-motion="1"] .sp-ctl-thumb { transition: none; }
`

function ensureStyle(doc: Document): void {
  if (doc.getElementById(STYLE_ID)) return
  const style = doc.createElement('style')
  style.id = STYLE_ID
  style.textContent = CSS
  doc.head.appendChild(style)
}

const EMPTY_VIEW: ControlBarView = {
  phase: 'paused',
  storyT: 0,
  storyDuration: 0,
  sceneStarts: [0],
  scrubbing: false,
  needsGesture: true,
  available: false,
  keysEnabled: false,
  reducedMotion: false,
  subtitles: false,
  rate: 1,
}

type FullscreenDoc = Document & {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
  webkitFullscreenEnabled?: boolean
}
type FullscreenEl = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void }
/** `lock` is Chrome-only and missing from TypeScript's DOM lib. */
type LockableOrientation = ScreenOrientation & { lock?: (o: 'landscape') => Promise<void> }

/**
 * The dock (play, the two skips, the line and its times, speed, subtitles,
 * full screen), the `?` sheet and a one-line on-screen confirmation ("Sound
 * off"). `elements` are inserted by the HUD, in order, above the pause overlay
 * and below the grown-up panel.
 */
export class ControlBar {
  readonly bar: HTMLElement
  readonly help: HTMLElement
  readonly osd: HTMLElement

  private readonly doc: Document
  private readonly host: HTMLElement
  private readonly actions: ControlBarActions
  private readonly hideAfterMs: number
  private readonly disposers: (() => void)[] = []

  private readonly time: HTMLElement
  private readonly playBtn: HTMLButtonElement
  private readonly backBtn: HTMLButtonElement
  private readonly forwardBtn: HTMLButtonElement
  private readonly ccBtn: HTMLButtonElement
  private readonly fullscreenBtn: HTMLButtonElement
  private lastPlayIcon = ''
  private lastCc = ''
  private lastFullscreen = ''
  /** The browser can put the host itself in full screen (not an iPhone's Safari). */
  private readonly nativeFullscreen: boolean
  /** The host fills the window in place of full screen. */
  private pseudoFullscreen = false
  /** This bar locked the screen to landscape and owes the unlock. */
  private orientationLocked = false
  /** The window-filling full screen is drawn sideways (`ROTATED_ATTR`): the seek line runs down the screen. */
  private rotated = false
  /** The resize this bar dispatches itself, so its own listener does not answer it. */
  private relayingOut = false
  private readonly track: HTMLElement
  private readonly fill: HTMLElement
  private readonly thumb: HTMLElement
  /** The track-wide layer the thumb rides on (moved by `transform`). */
  private readonly thumbLayer: HTMLElement
  private readonly rail: HTMLElement
  private readonly helpClose: HTMLButtonElement
  private readonly speedWrap: HTMLElement
  private readonly speedBtn: HTMLButtonElement
  private readonly speedMenu: HTMLElement
  private readonly speedItems: HTMLButtonElement[] = []
  private menuOpen = false
  private lastRateText = ''
  private lastFillKey = -1
  private lastDisabled = ''
  private lastReducedMotion = ''
  private lastTimeWidth = ''

  private view: ControlBarView = { ...EMPTY_VIEW }
  private placement: ControlBarPlacement | null = null
  /** CSS px the bar takes for the current placement (`controlBarHeight`). */
  private height = controlBarHeight(0, 'over')
  private shown = false
  private engaged: boolean
  private recentlyActive = false
  private hovering = false
  private focusWithin = false
  private helpOpen = false
  private hideTimer: ReturnType<typeof setTimeout> | null = null
  private osdTimer: ReturnType<typeof setTimeout> | null = null
  private destroyed = false

  private dragging = false
  private dragPointer: number | null = null
  private dragRect: TrackRect = { left: 0, width: 0 }
  private dragT = 0

  private lastTimeText = ''
  private lastValueNow = -1
  private lastMarksKey = ''

  constructor(doc: Document, opts: ControlBarOptions) {
    this.doc = doc
    this.host = opts.host
    this.actions = opts.actions
    this.hideAfterMs = opts.hideAfterMs ?? CONTROLS_HIDE_MS
    this.engaged = opts.engaged ?? true
    ensureStyle(doc)

    const el = (tag: string, cls: string, hud?: string): HTMLElement => {
      const node = doc.createElement(tag)
      node.className = cls
      if (hud) node.dataset.hud = hud
      return node
    }

    this.bar = el('div', 'sp-ctl', 'controls')
    this.bar.dataset.shown = '0'
    this.bar.dataset.mode = 'over'
    this.bar.dataset.size = 'narrow'
    this.bar.setAttribute('role', 'group')
    this.bar.setAttribute('aria-label', 'Story playback')

    this.track = el('div', 'sp-ctl-seek', 'seek')
    this.track.tabIndex = 0
    this.track.setAttribute('role', 'slider')
    this.track.setAttribute('aria-label', 'Story position')
    this.track.setAttribute('aria-orientation', 'horizontal')
    this.track.setAttribute('aria-valuemin', '0')
    this.rail = el('div', 'sp-ctl-rail')
    this.fill = el('div', 'sp-ctl-fill')
    this.thumbLayer = el('div', 'sp-ctl-thumbs')
    this.thumb = el('div', 'sp-ctl-thumb')
    this.rail.appendChild(this.fill)
    this.thumbLayer.appendChild(this.thumb)
    this.track.append(this.rail, this.thumbLayer)

    this.time = el('span', 'sp-ctl-time', 'time')
    // The slider carries the same information as spoken text; reading both is noise.
    this.time.setAttribute('aria-hidden', 'true')

    const button = (hud: string, label: string, icon = ''): HTMLButtonElement => {
      const b = doc.createElement('button')
      b.type = 'button'
      b.className = 'sp-ctl-btn'
      b.dataset.hud = hud
      b.setAttribute('aria-label', label)
      b.title = label
      if (icon) b.innerHTML = icon
      return b
    }
    this.playBtn = button('play-pause', 'Play')
    this.backBtn = button('back', 'Back 10 seconds', ICON.back)
    this.backBtn.title = `Back ${SEEK_JUMP_S} seconds (J)`
    this.forwardBtn = button('forward', 'Forward 10 seconds', ICON.forward)
    this.forwardBtn.title = `Forward ${SEEK_JUMP_S} seconds (L)`
    this.ccBtn = button('subtitles', 'Subtitles')
    const cc = el('span', 'sp-ctl-cc')
    cc.textContent = 'CC'
    cc.setAttribute('aria-hidden', 'true')
    this.ccBtn.appendChild(cc)
    this.ccBtn.setAttribute('aria-pressed', 'false')
    this.fullscreenBtn = button('fullscreen', 'Full screen', ICON.expand)
    this.fullscreenBtn.setAttribute('aria-pressed', 'false')
    // An iPhone's Safari has no full screen for an element; there the player fills the window
    // instead (`setPseudoFullscreen`), so the button is there on every phone.
    const fsEl = this.host as FullscreenEl
    const fsDoc = doc as FullscreenDoc
    this.nativeFullscreen =
      (typeof fsEl.requestFullscreen === 'function' || typeof fsEl.webkitRequestFullscreen === 'function') &&
      doc.fullscreenEnabled !== false &&
      fsDoc.webkitFullscreenEnabled !== false
    this.bar.dataset.fullscreen = '1'

    // Speed: "1×" and its menu, which opens upwards over the picture.
    this.speedWrap = el('span', 'sp-ctl-speed')
    this.speedBtn = button('speed', 'Playback speed')
    this.speedBtn.classList.add('sp-ctl-rate')
    this.speedBtn.style.minWidth = `max(${CONTROL_HIT_PX}px, ${RATE_LABEL_EM}em)`
    this.speedBtn.setAttribute('aria-haspopup', 'menu')
    this.speedBtn.setAttribute('aria-expanded', 'false')
    this.speedMenu = el('div', 'sp-ctl-menu', 'speed-menu')
    this.speedMenu.setAttribute('role', 'menu')
    this.speedMenu.setAttribute('aria-label', 'Playback speed')
    this.speedMenu.hidden = true
    const head = el('div', 'sp-ctl-menu-head')
    head.textContent = 'Speed'
    head.setAttribute('aria-hidden', 'true')
    this.speedMenu.appendChild(head)
    for (const r of PLAYBACK_RATES) {
      const item = doc.createElement('button')
      item.type = 'button'
      item.tabIndex = -1
      item.dataset.hud = 'speed-option'
      item.dataset.rate = String(r)
      item.setAttribute('role', 'menuitemradio')
      item.setAttribute('aria-checked', 'false')
      // The check mark is decoration (aria-checked says it); kept out of the item's name.
      const check = doc.createElement('span')
      check.className = 'sp-ctl-check'
      check.setAttribute('aria-hidden', 'true')
      item.append(check, doc.createTextNode(r === 1 ? '1× Normal' : formatRate(r)))
      this.speedItems.push(item)
      this.speedMenu.appendChild(item)
    }
    this.speedWrap.append(this.speedBtn, this.speedMenu)
    // A HUD that cannot change the speed shows no speed control.
    this.speedWrap.hidden = typeof opts.actions.setRate !== 'function'

    const dock = el('div', 'sp-ctl-dock')
    const transport = el('div', 'sp-ctl-transport')
    transport.append(this.playBtn, this.backBtn, this.forwardBtn)
    const timeline = el('div', 'sp-ctl-timeline')
    timeline.append(this.track, this.time)
    const side = el('div', 'sp-ctl-side')
    side.append(this.speedWrap, this.ccBtn, this.fullscreenBtn)
    dock.append(transport, timeline, side)
    this.bar.appendChild(dock)

    this.osd = el('div', 'sp-ctl-osd', 'osd')
    this.osd.dataset.shown = '0'
    this.osd.setAttribute('role', 'status')
    this.osd.setAttribute('aria-live', 'polite')

    this.help = el('div', 'sp-ctl-help', 'shortcuts')
    this.help.setAttribute('role', 'dialog')
    this.help.setAttribute('aria-label', 'Keyboard shortcuts')
    this.help.hidden = true
    const helpHead = el('div', 'sp-ctl-help-head')
    const h = doc.createElement('h2')
    h.textContent = 'Keyboard shortcuts'
    this.helpClose = doc.createElement('button')
    this.helpClose.type = 'button'
    this.helpClose.dataset.hud = 'shortcuts-close'
    this.helpClose.textContent = 'Close'
    helpHead.append(h, this.helpClose)
    const table = doc.createElement('table')
    const body = doc.createElement('tbody')
    for (const [keys, what] of SHORTCUTS) {
      const tr = doc.createElement('tr')
      const th = doc.createElement('th')
      th.scope = 'row'
      th.textContent = keys
      const td = doc.createElement('td')
      td.textContent = what
      tr.append(th, td)
      body.appendChild(tr)
    }
    table.appendChild(body)
    this.help.append(helpHead, table)

    this.wire()
    this.render()
  }

  /** In stacking order: the bar, the confirmation, the `?` sheet. */
  get elements(): HTMLElement[] {
    return [this.bar, this.osd, this.help]
  }

  get isShown(): boolean {
    return this.shown
  }

  get isHelpOpen(): boolean {
    return this.helpOpen
  }

  /** CSS px the bar currently covers at the bottom of the picture (0 when hidden or below). */
  get overInset(): number {
    return this.shown && this.placement?.mode === 'over' ? this.height : 0
  }

  /**
   * CSS px reserved directly under the picture. Held even while the bar is
   * hidden: a portrait subtitle band that jumped every three seconds would be
   * worse than a quiet strip of mat.
   */
  get belowInset(): number {
    return this.view.available && this.placement?.mode === 'below' ? this.height : 0
  }

  /* ----------------------------------------------------------- wiring */

  private listen(target: EventTarget, type: string, handler: (e: Event) => void, opts?: AddEventListenerOptions | boolean): void {
    target.addEventListener(type, handler, opts)
    this.disposers.push(() => target.removeEventListener(type, handler, opts))
  }

  private wire(): void {
    // The one rule that matters most: nothing that lands on the bar or the
    // sheet may reach anything that could read it as a stage tap.
    const stop = (e: Event): void => e.stopPropagation()
    for (const node of [this.bar, this.help]) {
      for (const type of ['pointerdown', 'pointerup', 'click', 'dblclick', 'touchstart', 'touchend', 'contextmenu']) {
        this.listen(node, type, stop)
      }
    }
    this.listen(this.bar, 'pointerdown', () => this.poke())
    this.listen(this.playBtn, 'click', () => {
      this.poke()
      this.actions.togglePlay()
    })
    this.listen(this.backBtn, 'click', () => {
      if (this.seekEnabled()) this.seekTo(this.view.storyT - SEEK_JUMP_S)
    })
    this.listen(this.forwardBtn, 'click', () => {
      if (this.seekEnabled()) this.seekTo(this.view.storyT + SEEK_JUMP_S)
    })
    this.listen(this.ccBtn, 'click', () => {
      this.poke()
      const on = this.actions.toggleSubtitles()
      this.ccBtn.setAttribute('aria-pressed', on ? 'true' : 'false')
      this.flash(on ? 'Subtitles on' : 'Subtitles off')
    })
    this.listen(this.fullscreenBtn, 'click', () => {
      this.poke()
      this.toggleFullscreen()
    })
    for (const type of ['fullscreenchange', 'webkitfullscreenchange']) {
      this.listen(this.doc, type, () => {
        if (!this.fullscreenElement()) this.unlockOrientation()
        this.render()
      })
    }
    // A phone turned while the window-filling full screen is on: sideways only while upright.
    // (In a frame that the page has just pinned over the screen, this is also where the frame's
    // new size arrives.)
    const win = this.doc.defaultView
    if (win) {
      for (const type of ['resize', 'orientationchange']) {
        this.listen(win, type, () => {
          if (!this.relayingOut && this.pseudoFullscreen) this.syncRotation()
        })
      }
      // The page around a framed player ends its full screen (Escape there, the page leaving).
      if (win.parent && win.parent !== win) {
        this.listen(win, 'message', (e) => {
          const m = e as MessageEvent
          const data = m.data as { type?: unknown; on?: unknown } | null
          if (m.source === win.parent && data?.type === FRAME_FULLSCREEN_MESSAGE && data.on === false) {
            this.setPseudoFullscreen(false)
          }
        })
      }
    }

    // Speed menu (menu-button pattern): the button toggles it, ↑ / ↓ open it on
    // the current speed, an item applies and closes, Escape / Tab / a pointer
    // anywhere else close it.
    this.listen(this.speedBtn, 'click', () => {
      this.poke()
      this.setMenu(!this.menuOpen)
    })
    this.listen(this.speedBtn, 'keydown', (e) => {
      const key = (e as KeyboardEvent).key
      if (key !== 'ArrowUp' && key !== 'ArrowDown') return
      e.preventDefault()
      this.setMenu(true)
    })
    for (const item of this.speedItems) {
      this.listen(item, 'click', (e) => {
        e.preventDefault()
        this.chooseRate(Number(item.dataset.rate))
      })
    }
    this.listen(this.speedMenu, 'keydown', (e) => this.onMenuKey(e as KeyboardEvent))

    this.listen(this.bar, 'pointerenter', (e) => {
      if ((e as PointerEvent).pointerType === 'mouse') this.setFlag('hovering', true)
    })
    this.listen(this.bar, 'pointerleave', () => this.setFlag('hovering', false))
    // Keyboard focus pins the bar open (a grown-up tabbing to the slider must
    // not watch it vanish); focus left behind by a click or a drag does not.
    this.listen(this.bar, 'focusin', (e) => {
      let keyboard = true
      try {
        keyboard = (e.target as Element).matches(':focus-visible')
      } catch {
        /* an engine without :focus-visible: treat all focus as keyboard focus */
      }
      if (keyboard) this.setFlag('focusWithin', true)
    })
    this.listen(this.bar, 'focusout', (e) => {
      const next = (e as FocusEvent).relatedTarget as Node | null
      if (!next || !this.bar.contains(next)) this.setFlag('focusWithin', false)
    })

    // Seek: pointer capture, so a drag that leaves the bar keeps seeking and a
    // finger that slides off the phone's edge still lets go cleanly.
    this.listen(this.track, 'pointerdown', (e) => this.onTrackDown(e as PointerEvent))
    this.listen(this.track, 'pointermove', (e) => this.onTrackMove(e as PointerEvent))
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      this.listen(this.track, type, (e) => this.onTrackUp(e as PointerEvent))
    }
    this.listen(this.track, 'keydown', (e) => this.onTrackKey(e as KeyboardEvent))

    this.listen(this.helpClose, 'click', (e) => {
      e.preventDefault()
      this.setHelp(false)
    })

    // Activity: a mouse moving over the player, or any touch on it, shows the
    // bar. A stage tap is still a stage tap — this only watches it go by.
    this.listen(this.host, 'pointermove', () => this.poke(), { passive: true })
    this.listen(this.host, 'pointerdown', () => this.poke(), { passive: true })

    // Scope for the hotkeys: the last thing the grown-up touched.
    this.listen(
      this.doc,
      'pointerdown',
      (e) => {
        const target = e.target as Node | null
        this.engaged = !!target && this.host.contains(target)
        if (this.menuOpen && !(target && this.speedWrap.contains(target))) this.setMenu(false)
      },
      true,
    )
    this.listen(this.doc, 'keydown', (e) => this.onKey(e as KeyboardEvent))
  }

  private setFlag(flag: 'hovering' | 'focusWithin', on: boolean): void {
    if (this[flag] === on) return
    this[flag] = on
    if (!on) this.poke()
    else this.sync(true)
  }

  /* --------------------------------------------------------- the seek */

  private total(): number {
    const T = this.view.storyDuration
    return Number.isFinite(T) && T > 0 ? T : 0
  }

  private seekEnabled(): boolean {
    return this.view.available && !this.view.needsGesture && this.total() > 0
  }

  private onTrackDown(e: PointerEvent): void {
    e.preventDefault() // no text selection, no focus theft by the canvas, no iOS magnifier
    if (e.pointerType === 'mouse' && e.button !== 0) return
    if (!this.seekEnabled() || this.dragging) return
    try {
      this.track.focus({ preventScroll: true })
    } catch {
      /* an old engine without focus options */
    }
    try {
      this.track.setPointerCapture(e.pointerId)
    } catch {
      /* synthetic events in tests have no active pointer */
    }
    const r = this.track.getBoundingClientRect()
    // Turned a quarter clockwise, the line's start is at the top of the screen and its end at the bottom.
    this.dragRect = this.rotated ? { left: r.top, width: r.height } : { left: r.left, width: r.width }
    this.dragging = true
    this.dragPointer = e.pointerId
    this.dragT = timeAtPointer(this.rotated ? e.clientY : e.clientX, this.dragRect, this.total())
    this.bar.dataset.dragging = '1'
    this.actions.seek({ t: this.dragT, scrub: 'start' })
    this.render()
    this.sync(true)
  }

  private onTrackMove(e: PointerEvent): void {
    if (!this.dragging || e.pointerId !== this.dragPointer) return
    const t = timeAtPointer(this.rotated ? e.clientY : e.clientX, this.dragRect, this.total())
    if (Math.abs(t - this.dragT) < 1e-3) return
    this.dragT = t
    this.actions.seek({ t, scrub: 'move' })
    this.render()
  }

  private onTrackUp(e: PointerEvent): void {
    if (!this.dragging || (e.pointerId !== this.dragPointer && e.type !== 'lostpointercapture')) return
    this.dragging = false
    this.dragPointer = null
    delete this.bar.dataset.dragging
    try {
      if (this.track.hasPointerCapture?.(e.pointerId)) this.track.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
    this.actions.seek({ t: this.dragT, scrub: 'end' })
    this.render()
    this.poke()
  }

  /** The slider's own keys, per the ARIA slider pattern. Marked handled so the page hotkeys skip them. */
  private onTrackKey(e: KeyboardEvent): void {
    if (e.ctrlKey || e.metaKey || e.altKey || !this.seekEnabled()) return
    const now = this.view.storyT
    let t: number | null = null
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        t = now - SEEK_STEP_S
        break
      case 'ArrowRight':
      case 'ArrowUp':
        t = now + SEEK_STEP_S
        break
      case 'PageDown':
        t = now - SEEK_JUMP_S
        break
      case 'PageUp':
        t = now + SEEK_JUMP_S
        break
      case 'Home':
        t = 0
        break
      case 'End':
        t = this.total()
        break
      default:
        return
    }
    e.preventDefault()
    this.seekTo(t)
  }

  private seekTo(t: number): void {
    this.actions.seek({ t: clamp(t, 0, this.total()) })
    this.poke()
  }

  /* ----------------------------------------------------------- hotkeys */

  /** Keys belong to the player only while it is on the page and is what the grown-up is using. */
  private inScope(): boolean {
    if (this.destroyed || !this.host.isConnected) return false
    const active = this.doc.activeElement
    if (active && active !== this.doc.body && active !== this.doc.documentElement) return this.host.contains(active)
    return this.engaged
  }

  private onKey(e: KeyboardEvent): void {
    if (!this.inScope()) return
    const action = hotkeyFor(e)
    if (!action) return
    if (action.kind === 'escape') {
      if (this.menuOpen) {
        e.preventDefault()
        this.setMenu(false, true)
        return
      }
      if (this.helpOpen) {
        e.preventDefault()
        this.setHelp(false)
        return
      }
      // Real full screen ends on Escape by itself; the window-filling kind has to be told.
      if (this.pseudoFullscreen) {
        e.preventDefault()
        this.setPseudoFullscreen(false)
      }
      return // otherwise Escape belongs to whoever else wants it (the panel, the page)
    }
    if (!this.view.keysEnabled) return
    // Before the first gesture only the grown-up's "go" and the two page-level
    // keys mean anything; there is no story to seek in yet.
    if (this.view.needsGesture && action.kind !== 'toggle' && action.kind !== 'help' && action.kind !== 'fullscreen') return
    e.preventDefault() // Space and the arrows would scroll the page underneath
    this.run(action)
  }

  private run(action: HotkeyAction): void {
    switch (action.kind) {
      case 'toggle':
        this.actions.togglePlay()
        break
      case 'seekBy':
        if (this.seekEnabled()) this.seekTo(this.view.storyT + action.seconds)
        break
      case 'seekTo':
        if (this.seekEnabled()) this.seekTo(action.where === 'start' ? 0 : this.total())
        break
      case 'scene':
        if (this.seekEnabled()) this.actions.scene(action.delta)
        break
      case 'mute':
        this.flash(this.actions.toggleMute() ? 'Sound off' : 'Sound on')
        break
      case 'subtitles':
        this.flash(this.actions.toggleSubtitles() ? 'Subtitles on' : 'Subtitles off')
        break
      case 'speed':
        if (this.actions.setRate) this.chooseRate(stepRate(this.view.rate ?? 1, action.delta))
        break
      case 'fullscreen':
        this.toggleFullscreen()
        break
      case 'help':
        this.setHelp(!this.helpOpen)
        break
      default:
        break
    }
    this.poke()
  }

  private fullscreenElement(): Element | null {
    const doc = this.doc as FullscreenDoc
    return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
  }

  /** In full screen of either kind. */
  private isFullscreen(): boolean {
    return this.pseudoFullscreen || this.fullscreenElement() !== null
  }

  /**
   * Real full screen where the browser has it for an element (desktop, Android,
   * iPad) — on a phone, turned to landscape too, since the picture is 16:9. An
   * iPhone's Safari has none, and a request that fails anywhere falls back the
   * same way: the player fills the window (`setPseudoFullscreen`).
   */
  private toggleFullscreen(): void {
    if (this.pseudoFullscreen) {
      this.setPseudoFullscreen(false)
      return
    }
    const doc = this.doc as FullscreenDoc
    const fallback = (): void => this.setPseudoFullscreen(true)
    try {
      if (this.fullscreenElement()) {
        const exit = doc.exitFullscreen ?? doc.webkitExitFullscreen
        const done = exit?.call(doc)
        if (done && typeof (done as Promise<void>).catch === 'function') (done as Promise<void>).catch(() => undefined)
        return
      }
      if (!this.nativeFullscreen) {
        fallback()
        return
      }
      const el = this.host as FullscreenEl
      // Android hides its navigation bar for `navigationUI: 'hide'`; the prefixed call takes no options.
      const done = el.requestFullscreen
        ? el.requestFullscreen({ navigationUI: 'hide' })
        : el.webkitRequestFullscreen?.call(el)
      if (done && typeof (done as Promise<void>).then === 'function') {
        ;(done as Promise<void>).then(() => this.lockLandscape(), fallback)
      } else {
        this.lockLandscape()
      }
    } catch {
      fallback()
    }
  }

  /** A phone in full screen turns to landscape (Chrome on Android; elsewhere `lock` is absent or refuses). */
  private lockLandscape(): void {
    const win = this.doc.defaultView
    const orientation = win?.screen?.orientation as LockableOrientation | undefined
    if (!orientation?.lock || !win?.matchMedia?.('(pointer: coarse)').matches) return
    orientation.lock('landscape').then(
      () => {
        this.orientationLocked = true
      },
      () => undefined,
    )
  }

  private unlockOrientation(): void {
    if (!this.orientationLocked) return
    this.orientationLocked = false
    try {
      this.doc.defaultView?.screen?.orientation?.unlock()
    } catch {
      /* already unlocked with full screen */
    }
  }

  /**
   * The window-filling stand-in for full screen (an iPhone's Safari): the host is
   * fixed over the whole window, the page under it stops scrolling, and on an
   * upright phone the story is drawn sideways to fill the screen
   * (`syncRotation`). Safari's own bars stay; only a home-screen web app loses
   * those on an iPhone.
   */
  private setPseudoFullscreen(on: boolean): void {
    if (this.pseudoFullscreen === on) return
    this.pseudoFullscreen = on
    const root = this.doc.documentElement
    if (on) {
      this.host.setAttribute(PSEUDO_FULLSCREEN_ATTR, '1')
      root.setAttribute(`${PSEUDO_FULLSCREEN_ATTR}-lock`, '')
    } else {
      this.host.removeAttribute(PSEUDO_FULLSCREEN_ATTR)
      root.removeAttribute(`${PSEUDO_FULLSCREEN_ATTR}-lock`)
    }
    // In a frame, "the window" is only the frame's box: the page around it is asked to pin
    // the frame over the screen (`FRAME_FULLSCREEN_MESSAGE`). Nothing secret is in it, so any
    // page may hear it; a page that does not answer leaves the player filling its frame.
    const win = this.doc.defaultView
    if (win?.parent && win.parent !== win) {
      try {
        win.parent.postMessage({ type: FRAME_FULLSCREEN_MESSAGE, on }, '*')
      } catch {
        /* a parent gone mid-navigation */
      }
    }
    this.syncRotation(false)
    this.relayoutAll()
  }

  /**
   * Sideways while the window-filling full screen is on an upright phone; upright again the
   * moment the phone is turned (with rotation lock off the window itself goes landscape).
   * Returns whether it changed.
   */
  private syncRotation(relayout = true): boolean {
    const win = this.doc.defaultView
    const want =
      this.pseudoFullscreen &&
      !!win &&
      win.innerHeight > win.innerWidth &&
      win.matchMedia?.('(pointer: coarse)').matches === true
    if (want === this.rotated) return false
    this.rotated = want
    if (want) this.host.setAttribute(ROTATED_ATTR, '1')
    else this.host.removeAttribute(ROTATED_ATTR)
    if (relayout) this.relayoutAll()
    return true
  }

  /**
   * The stage and the HUD re-measure on their own (ResizeObserver on the host); the renderer
   * listens for the window's resize, which a host-only change never fires.
   */
  private relayoutAll(): void {
    this.relayingOut = true
    try {
      this.doc.defaultView?.dispatchEvent(new Event('resize'))
    } finally {
      this.relayingOut = false
    }
    this.render()
    this.actions.changed()
  }

  private setHelp(open: boolean): void {
    if (this.helpOpen === open) return
    this.helpOpen = open
    this.help.hidden = !open
    if (open) {
      try {
        this.helpClose.focus({ preventScroll: true })
      } catch {
        /* ignore */
      }
    }
    this.sync(true)
  }

  /* ------------------------------------------------------------ speed */

  /** Apply a speed (menu item or `<` / `>`), confirm it on screen and close the menu. */
  private chooseRate(rate: number): void {
    const set = this.actions.setRate
    if (!set) return
    const applied = clampRate(set(clampRate(rate)))
    // Mirror it now: a second `>` before the next state push steps from here.
    this.view = { ...this.view, rate: applied }
    const wasOpen = this.menuOpen
    this.setMenu(false, wasOpen)
    this.render()
    this.flash(`Speed ${formatRate(applied)}`)
    this.poke()
  }

  private setMenu(open: boolean, returnFocus = false): void {
    if (open && (this.speedWrap.hidden || !this.view.available)) return
    if (this.menuOpen === open) return
    this.menuOpen = open
    this.speedMenu.hidden = !open
    this.speedBtn.setAttribute('aria-expanded', open ? 'true' : 'false')
    if (open) {
      const current = this.speedItems.find((i) => i.getAttribute('aria-checked') === 'true') ?? this.speedItems[0]
      try {
        current?.focus({ preventScroll: true })
      } catch {
        /* ignore */
      }
    } else if (returnFocus) {
      try {
        this.speedBtn.focus({ preventScroll: true })
      } catch {
        /* ignore */
      }
    }
    this.sync(true)
  }

  /** ↑ / ↓ / Home / End move through the items, Escape closes, Tab leaves. Handled keys stay out of the hotkeys. */
  private onMenuKey(e: KeyboardEvent): void {
    const items = this.speedItems
    const at = items.indexOf(this.doc.activeElement as HTMLButtonElement)
    let next = -1
    switch (e.key) {
      case 'ArrowDown':
        next = at < 0 ? 0 : (at + 1) % items.length
        break
      case 'ArrowUp':
        next = at < 0 ? items.length - 1 : (at - 1 + items.length) % items.length
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = items.length - 1
        break
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        this.setMenu(false, true)
        return
      case 'Tab':
        this.setMenu(false)
        return
      default:
        return
    }
    e.preventDefault()
    try {
      items[next]?.focus({ preventScroll: true })
    } catch {
      /* ignore */
    }
  }

  get isMenuOpen(): boolean {
    return this.menuOpen
  }

  /** One short confirmation for a change that has no picture of its own (mute, subtitles, speed). */
  private flash(text: string): void {
    this.osd.textContent = text
    this.osd.dataset.shown = '1'
    if (this.osdTimer !== null) clearTimeout(this.osdTimer)
    this.osdTimer = setTimeout(() => {
      this.osdTimer = null
      this.osd.dataset.shown = '0'
    }, 1400)
  }

  /* ------------------------------------------------------- visibility */

  /** Something happened: show the bar and restart the hide countdown. */
  poke(): void {
    if (this.destroyed) return
    this.recentlyActive = true
    if (this.hideTimer !== null) clearTimeout(this.hideTimer)
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null
      this.recentlyActive = false
      this.sync(true)
    }, this.hideAfterMs)
    this.sync(true)
  }

  private wantShown(): boolean {
    const v = this.view
    if (!v.available || this.total() <= 0) return false
    if (this.dragging || v.scrubbing || this.helpOpen || this.menuOpen) return true
    // Paused, at a hold, at the end: nothing is moving, so nothing is covered.
    if (v.phase !== 'playing') return true
    return this.recentlyActive || this.hovering || this.focusWithin
  }

  /** Recompute visibility; `notify` asks the HUD to re-lay out when it changed. */
  private sync(notify: boolean): void {
    if (this.destroyed) return
    if (!this.view.available && this.menuOpen) {
      this.menuOpen = false
      this.speedMenu.hidden = true
      this.speedBtn.setAttribute('aria-expanded', 'false')
    }
    const next = this.wantShown()
    if (!this.view.available && this.helpOpen) {
      this.helpOpen = false
      this.help.hidden = true
    }
    if (next === this.shown) return
    this.shown = next
    this.bar.dataset.shown = next ? '1' : '0'
    this.bar.setAttribute('aria-hidden', next ? 'false' : 'true')
    if (notify) this.actions.changed()
  }

  /* ----------------------------------------------------------- render */

  /** The HUD's state push. Never calls back into the HUD: its render lays out right after. */
  setState(view: ControlBarView): void {
    if (this.destroyed) return
    const wasAvailable = this.view.available
    this.view = view
    const rm = view.reducedMotion || view.lowDistraction ? '1' : '0'
    if (rm !== this.lastReducedMotion) {
      this.lastReducedMotion = rm
      this.bar.dataset.reducedMotion = rm
    }
    // Becoming available (the start screen lifted) counts as activity, so the
    // grown-up sees the bar once and learns where it lives.
    if (view.available && !wasAvailable) {
      this.recentlyActive = true
      if (this.hideTimer !== null) clearTimeout(this.hideTimer)
      this.hideTimer = setTimeout(() => {
        this.hideTimer = null
        this.recentlyActive = false
        this.sync(true)
      }, this.hideAfterMs)
    }
    this.sync(false)
    this.render()
  }

  /** Called from the HUD's layout pass with the stage geometry. */
  place(p: ControlBarPlacement): void {
    this.placement = p
    const px = (n: number): string => `${Math.round(n)}px`
    const size = p.width >= DOCK_ONE_ROW_PX ? 'wide' : p.width > 0 && p.width < TINY_BAR_PX ? 'tiny' : 'narrow'
    this.bar.dataset.mode = p.mode
    this.bar.dataset.size = size
    this.height = controlBarHeight(p.width, p.mode)
    this.bar.style.left = px(p.left)
    this.bar.style.width = px(Math.max(0, p.width))
    this.bar.style.height = px(this.height)
    this.bar.style.top = px(p.mode === 'over' ? p.stageBottom - this.height : p.stageBottom)
    // On a phone the line runs edge to edge: keep the 18 px thumb at 0:00 off the bar's edge.
    this.track.style.margin = size === 'wide' ? '' : `0 ${px(NARROW_TRACK_MARGIN_PX)}`
    // The menu opens upwards over the picture — unless the picture is too short
    // for it (a portrait phone: 219 px of stage over 600 px of letterbox).
    const barTop = p.mode === 'over' ? p.stageBottom - this.height : p.stageBottom
    const roomAbove = barTop + this.height - CONTROL_HIT_PX
    const roomBelow = p.hostHeight - barTop - this.height
    const menuH = PLAYBACK_RATES.length * 42 + 40
    this.speedMenu.dataset.dir = roomAbove < menuH && roomBelow > roomAbove ? 'down' : 'up'
    this.osd.style.fontSize = px(Math.max(16, Math.round(p.textPx * 0.75)))
    this.osd.style.top = px(p.stageTop + 16)
    this.help.style.fontSize = px(p.textPx)
    this.render()
  }

  private render(): void {
    const total = this.total()
    const t = clamp(this.dragging ? this.dragT : this.view.storyT, 0, total)
    const f = total > 0 ? t / total : 0

    const text = `${formatTime(t)} / ${formatTime(total)}`
    if (text !== this.lastTimeText) {
      this.lastTimeText = text
      this.time.textContent = text
    }
    // Wide enough for the story's whole length on both sides, so the line does
    // not twitch when 9:59 becomes 10:00.
    const widest = formatTime(total).replace(/\d/g, '0')
    const timeWidth = `${timeTextEm(`${widest} / ${widest}`).toFixed(2)}em`
    if (timeWidth !== this.lastTimeWidth) {
      this.lastTimeWidth = timeWidth
      this.time.style.minWidth = timeWidth
    }

    // Compositor-only: a scaled fill and a translated thumb layer. 1/10000 of the
    // track is below a pixel on any screen, so smaller moves are not written.
    const fillKey = Math.round(f * 10000)
    if (fillKey !== this.lastFillKey) {
      this.lastFillKey = fillKey
      const q = fillKey / 10000
      this.fill.style.transform = `scaleX(${q})`
      this.thumbLayer.style.transform = `translateX(${(q * 100).toFixed(2)}%)`
    }

    const now = Math.floor(t + 1e-6)
    if (now !== this.lastValueNow || this.track.getAttribute('aria-valuemax') !== String(Math.floor(total + 1e-6))) {
      this.lastValueNow = now
      this.track.setAttribute('aria-valuemax', String(Math.floor(total + 1e-6)))
      this.track.setAttribute('aria-valuenow', String(now))
      this.track.setAttribute('aria-valuetext', seekValueText(t, total))
    }
    const seekable = this.seekEnabled()
    const disabled = seekable ? 'false' : 'true'
    if (disabled !== this.lastDisabled) {
      this.lastDisabled = disabled
      this.track.setAttribute('aria-disabled', disabled)
      this.backBtn.disabled = !seekable
      this.forwardBtn.disabled = !seekable
    }

    const r = clampRate(this.view.rate ?? 1)
    const rateText = formatRate(r)
    if (rateText !== this.lastRateText) {
      this.lastRateText = rateText
      this.speedBtn.textContent = rateText
      this.speedBtn.setAttribute('aria-label', `Playback speed ${rateText}`)
      this.speedBtn.title = `Speed ${rateText} (< >)`
      this.speedBtn.dataset.changed = Math.abs(r - 1) > 1e-6 ? '1' : '0'
      for (const item of this.speedItems) {
        item.setAttribute('aria-checked', Math.abs(Number(item.dataset.rate) - r) < 1e-6 ? 'true' : 'false')
      }
    }

    // ❚❚ while playing, ↻ at the end, ▶ otherwise — what Space / K mean in this phase.
    const phase = this.view.phase
    const icon = phase === 'playing' ? 'pause' : phase === 'ended' ? 'replay' : 'play'
    if (icon !== this.lastPlayIcon) {
      this.lastPlayIcon = icon
      this.playBtn.innerHTML = ICON[icon]
      const label = icon === 'pause' ? 'Pause' : icon === 'replay' ? 'Play again' : 'Play'
      this.playBtn.setAttribute('aria-label', label)
      this.playBtn.title = `${label} (Space)`
    }
    const cc = this.view.subtitles ? 'true' : 'false'
    if (cc !== this.lastCc) {
      this.lastCc = cc
      this.ccBtn.setAttribute('aria-pressed', cc)
      this.ccBtn.title = this.view.subtitles ? 'Subtitles on (C)' : 'Subtitles off (C)'
    }
    const fs = this.isFullscreen() ? 'true' : 'false'
    if (fs !== this.lastFullscreen) {
      this.lastFullscreen = fs
      this.fullscreenBtn.setAttribute('aria-pressed', fs)
      this.fullscreenBtn.innerHTML = fs === 'true' ? ICON.collapse : ICON.expand
      const label = fs === 'true' ? 'Exit full screen' : 'Full screen'
      this.fullscreenBtn.setAttribute('aria-label', label)
      this.fullscreenBtn.title = `${label} (F)`
    }

    this.renderMarks(total)
  }

  /** Part boundaries, when the story has more than one part. */
  private renderMarks(total: number): void {
    const starts = this.view.sceneStarts
    const key = `${total}|${starts.join(',')}`
    if (key === this.lastMarksKey) return
    this.lastMarksKey = key
    for (const old of [...this.rail.querySelectorAll('.sp-ctl-mark')]) old.remove()
    if (total <= 0 || starts.length < 2) return
    for (let i = 1; i < starts.length; i += 1) {
      const at = starts[i] as number
      if (!(at > 0 && at < total)) continue
      const mark = this.doc.createElement('i')
      mark.className = 'sp-ctl-mark'
      mark.dataset.hud = 'scene-mark'
      mark.style.left = `${((at / total) * 100).toFixed(3)}%`
      this.rail.appendChild(mark)
    }
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    if (this.hideTimer !== null) clearTimeout(this.hideTimer)
    if (this.osdTimer !== null) clearTimeout(this.osdTimer)
    for (const d of this.disposers.splice(0)) d()
    for (const node of this.elements) node.remove()
    // A player closed while filling the window must not leave the page fixed and unscrollable.
    this.host.removeAttribute(PSEUDO_FULLSCREEN_ATTR)
    this.host.removeAttribute(ROTATED_ATTR)
    if (this.pseudoFullscreen) this.doc.documentElement.removeAttribute(`${PSEUDO_FULLSCREEN_ATTR}-lock`)
    this.pseudoFullscreen = false
    this.unlockOrientation()
  }
}
